import React, {useContext, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {ContextDomainSearchResult} from "../../context/ContextDomainSearchResultProvider";
import ComponentSearchedDomainRow from "../ComponentSearchedDomainRow/ComponentSearchedDomainRow";

import WhiteBox from "../../common/scss/WhiteBox.scss";
import styles from './ComponentDomainSearchAlternativesBlock.scss';
import Button from "../../common/primitives/Button";
import SearchDomainInput from "../../common/primitives/SearchDomainInput";
import Submit from "../../common/primitives/Submit";
import YellowErrorBlock from "../../common/primitives/YellowErrorBlock";
import LoadingRow from "../../common/primitives/LoadingRow";
import {ContextTLD} from "../../context/ContextTLDProvider";
import {__t} from '../../common/lang/text-resource';

/**
 * Генерира съобщението за заетите тлд-та.
 * .com, .net и .org са заети.
 *
 * @param {Array.<string>} takenTLDs
 * @param primaryDomainData
 * @param fromBottom
 * @returns {*}
 */
function getTakenTLDsMessage(takenTLDs, primaryDomainData, fromBottom) {
	if (primaryDomainData.length > 0) {
		const primaryDomainTLD = primaryDomainData[0].domain_tld;
		const index = takenTLDs.indexOf(primaryDomainTLD);
		if (index !== -1) takenTLDs.splice(index, 1);
	}
	return (
	  <div data-layout={fromBottom === true ? 'margin-top--md' : ''}>
		  {
			  takenTLDs.length === 1 ?
				<span><span className={styles.takenTLD}>.{takenTLDs[0]}</span> {__t('с това име е зает!')}</span> :
				<div>
					{
						takenTLDs.map((tld, index) => {
							return <div key={index} className={styles.takenContainer}>
								{takenTLDs.length - 1 === index ?
								  <span>&nbsp;{__t('и')} .<span
									className={styles.takenTLD}>{tld}</span>&nbsp;</span>
								  : takenTLDs.length - 2 === index ?
									<span>.<span className={styles.takenTLD}>{tld}</span></span>
									:
									<span>.<span className={styles.takenTLD}>{tld}</span>,&nbsp;</span>}
							</div>
						})
					}&nbsp;{__t('с това име са заети!')}
				</div>
		  }
	  </div>
	)
}

const ComponentDomainSearchAlternativesBlock = ({
													messages,
													continueAction,
													submitSearchAnother,
													eventRaise,
													promoCode
												}) => {

	/** @type ContextDomainSearchResultProvider */
	const searchResultContext = useContext(ContextDomainSearchResult);
	const takenTLDs = searchResultContext.takenTLDs; // заетите tld-та

	/**
	 * домейни които не са проверени или са свободни
	 * @type {Array.<DomainData>}
	 * */
	const notCheckedOrFreeDomains = searchResultContext.getNotCheckedOrFreeDomains();

	const cTLD = useContext(ContextTLD);

	/**
	 * домейни които не са алтернативи
	 * @type {Array.<DomainData>}
	 * */
	const notSuggestedDomainData = searchResultContext.getNotSuggestedDomainData();

	/**
	 * основен домейн, който е търсил клиента
	 * @type {Array.<DomainData>}
	 * */
	const primaryDomainData = searchResultContext.getPrimaryDomainData();

	// https://jira.superhosting.bg/browse/ORDER-43
	/**
	 * Използваме го за да показваме грешките при търсене на домейн
	 */
	const [domainNameError, setDomainNameError] = useState('');

	/**
	 * Използваме го за да показваме анимация при търсене на друг домейн
	 */
	const [innerLoading, setInnerLoading] = useState(false);

	const searchInputRef = useRef(null);
	/**
	 * Търсене на нов домейн.
	 *
	 * @param {Event} e
	 */
	const submitSearchForm = (e) => {
		e.preventDefault();
		setInnerLoading(true);
		const domainName = searchInputRef.current.value.toLowerCase().trim();


		//Валидации на домейна
		if (domainName === '') {
			setDomainNameError(messages.EMPTY_DOMAIN_NAME_ERROR);
			setInnerLoading(false);
			searchInputRef.current.focus();
			return;
		}
		try {
			cTLD.validateDomainName(domainName, false);
		} catch (e) {
			setDomainNameError(messages[e].replace('{%domainName%}', domainName));
			setInnerLoading(false);
			searchInputRef.current.focus();
			return;
		}


		submitSearchAnother(domainName, () => setInnerLoading(false));
	};

	const clearDomainNameError = () => {
		setDomainNameError('');
	};

	return (
	  <section className={`${WhiteBox.container} ${WhiteBox.paddingNone}`}
			   data-layout="padding-top--20 padding-bottom--md">
		  {/* Грешки от валидации */}
		  {
			domainNameError !== '' && <YellowErrorBlock error={domainNameError}/>
		  }
		  <table className={styles.alternativesTable}>
			  <tbody>
			  {/* Ако търсеният домейн от клиента е зает, съобщението за заети tld_та се показва тука */}
			  {
				(takenTLDs.length > 0 && notSuggestedDomainData.length > 0 && (!notSuggestedDomainData[0].is_free && !notSuggestedDomainData[0].searched_with_tld)) === true &&
				<tr data-test="taken-tld-message">
					<td colSpan={3}>
						<div className={styles.yellowBlock}>
							{getTakenTLDsMessage(takenTLDs, primaryDomainData, false)}
							{__t('Вижте алтернативите по-долу или потърсете друг домейн.')}
						</div>
					</td>
				</tr>
			  }
			  {/*
				* Бутони които се показват ако търсения домейн е зает. Показват се само при търсене без tld.
				* https://jira.superhosting.bg/browse/ORDER-43
				*/}
			  {
				(notSuggestedDomainData.length > 0 && !notSuggestedDomainData[0].is_free && primaryDomainData.length === 0) === true
				&& <tr>
					<td colSpan={3} data-layout="padding-left--15 padding-right--15">
						<form
						  onSubmit={submitSearchForm}
						  data-test="search-domain-form"
						  data-layout={`margin-top--15 margin-bottom--20`}>

							<SearchDomainInput
							  defaultValue={notSuggestedDomainData[0].domain_name}
							  inputName={'domainName'}
							  hasError={domainNameError !== ''}
							  placeholder={__t('домейн име')}
							  customRef={searchInputRef}
							  onChange={clearDomainNameError}/>
							<div data-layout={`text-center margin-top--sm`}>
								{
									innerLoading ? <LoadingRow/> : <Submit value={__t("Провери дали е свободен")}/>
								}
							</div>
						</form>
					</td>
				</tr>

			  }
			  {/* Показваме всеки домейн, който не е проверен или е свободен. */}
			  {
				  notCheckedOrFreeDomains.length > 0 ? notCheckedOrFreeDomains.map(domainData =>
					  <ComponentSearchedDomainRow
						key={domainData.id}
						promoCode={promoCode}
						domainData={domainData}
						messages={messages}
						continueAction={continueAction}
						submitSearchAnother={submitSearchAnother}
						eventRaise={eventRaise}
					  />) :
					<tr>
						<td>
							{__t('няма-намерени-свободни-домейни-моля-потърсете-дру22646a')}
						</td>
					</tr>
			  }

			  {/* Ако има заети TLD-та и търсения домейн не е зает, показваме съобщенитео за TLD-та тука */}
			  {
				(takenTLDs.length > 0 && notSuggestedDomainData.length > 0 && notSuggestedDomainData[0].searched_with_tld === 1) &&
				<tr>
					<td colSpan={3}>
						{getTakenTLDsMessage(takenTLDs, primaryDomainData, true)}
					</td>
				</tr>
			  }

			  {/* Бутони които се показват ако търсения домейн е зает. Показват се само при търсене без tld. */}
			  {
				(notSuggestedDomainData.length > 0 && !notSuggestedDomainData[0].is_free && primaryDomainData.length === 0) === true &&
				<tr className={styles.continueButtonContainer} data-test="alternatives-continue-button-container">
					<td colSpan={3}>
						<div data-layout="text-center margin-top--15">
							<Button onClick={continueAction} value={__t("Продължи с поръчката")}
									id={'page_domain_result_continue_02'}/>
						</div>
					</td>
				</tr>
			  }
			  </tbody>
		  </table>
	  </section>
	);
};

ComponentDomainSearchAlternativesBlock.propTypes = {
	messages: PropTypes.objectOf(PropTypes.string),
};
export default ComponentDomainSearchAlternativesBlock;